import type { DetectedSourceType } from "./createFlow";
import type { ProgressEvent, StageId } from "./runGeneration";

export interface StageNode { label: string; band: [number, number]; stageIds: StageId[]; puns: string[]; }

const PHOTO: StageNode[] = [
  { label: "Beam Up", band: [0, 14], stageIds: ["upload"], puns: ["Beaming you into orbit…", "Warping pixels up…"] },
  { label: "Gemini Cooks", band: [14, 55], stageIds: ["script"], puns: ["Gemini's reading the room…", "Calibrating the burn…", "Charging the roast core…"] },
  { label: "Voice", band: [55, 97], stageIds: ["voice"], puns: ["Warming up the vocal cords…", "Tuning the roast frequency…"] },
  { label: "Ignition", band: [97, 100], stageIds: ["assemble", "done"], puns: ["Overcharging…", "Ignition."] },
];
// `comprehend` reads the deck AND writes the roast in one slow Gemini call, so it owns the
// biggest band and spans the meter's Analyze→Roast sections (blue→yellow = "reading… roasting").
// `render` (fast, real N/M) gets a tight slice so it can't blast through a whole section.
const DECK: StageNode[] = [
  { label: "Beam Up", band: [0, 8], stageIds: ["upload"], puns: ["Beaming your deck into orbit…", "Warping the slides up…"] },
  { label: "Gemini Studies", band: [8, 50], stageIds: ["comprehend"], puns: ["Gemini's reading every slide…", "Consulting the star charts…", "Finding the cringe…"] },
  { label: "Light the Slides", band: [50, 64], stageIds: ["render"], puns: ["Illuminating the deck…", "Charging slide by slide…"] },
  { label: "Voice & Doodles", band: [64, 97], stageIds: ["voice"], puns: ["Juicing up the voice…", "Sketching cosmic doodles…", "Zeroing in…"] },
  { label: "Ignition", band: [97, 100], stageIds: ["assemble", "done"], puns: ["Overcharging…", "Stellar burns incoming.", "Ignition."] },
];

export const PROCESSING_STAGES: Record<DetectedSourceType, StageNode[]> = { photo: PHOTO, deck: DECK, doc: DECK };

export function nodeForCharge(type: DetectedSourceType, charge: number): StageNode {
  const nodes = PROCESSING_STAGES[type];
  return nodes.find((n) => charge >= n.band[0] && charge < n.band[1]) ?? nodes[nodes.length - 1];
}

/** Real-signal charge for an event with done/total, else null (opaque → ETA-crawl owns it). */
export function chargeFor(type: DetectedSourceType, e: ProgressEvent): number | null {
  if (e.done == null || e.total == null || e.total === 0) return null;
  const node = PROCESSING_STAGES[type].find((n) => n.stageIds.includes(e.stage));
  if (!node) return null;
  const [lo, hi] = node.band;
  return lo + (e.done / e.total) * (hi - lo);
}
